import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import keys from 'lodash/keys'
import toPairs from 'lodash/toPairs'

export type JsonPrimitive = boolean | number | string | null
export type JsonArray = JsonValue[]
export type JsonObject = { [x: string]: JsonValue | undefined }
export type JsonValue = JsonArray | JsonObject | JsonPrimitive

export function patchHasKey(patch: object, key: PropertyKey): boolean {
  return (patch as Record<PropertyKey, unknown>)[key] !== undefined
}

export function patchKeys(patch: object): string[] {
  return keys(patch).filter((key) => (patch as Record<string, unknown>)[key] !== undefined)
}

export function patchIsEmpty(patch: object): boolean {
  return patchKeys(patch).length === 0
}

export function assignWireClearField<T extends object>(
  target: T,
  patch: object,
  key: keyof T,
  wireValue: unknown
): void {
  if (!patchHasKey(patch, key)) return
  ;(target as Record<string, unknown>)[key as string] = wireValue === null ? undefined : wireValue
}

export function assignTrimmedField<T extends object>(target: T, key: keyof T, value: string | undefined): void {
  const trimmed = value?.trim()
  if (trimmed == null || trimmed.length === 0) return
  ;(target as Record<string, unknown>)[key as string] = trimmed
}

export function pruneMeta(value: unknown): unknown {
  if (value === undefined) return undefined
  if (value === null) return null

  if (isString(value)) {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
  }

  if (isArray(value)) {
    const prunedArray = value.map((item) => pruneMeta(item)).filter((item) => item !== undefined)
    return prunedArray
  }

  if (isObject(value)) {
    const result: Record<string, unknown> = {}
    for (const [key, val] of toPairs(value)) {
      const pruned = pruneMeta(val)
      if (pruned === undefined) continue
      if (isObject(pruned) && pruned !== null && !isArray(pruned) && keys(pruned).length === 0) {
        continue
      }
      result[key] = pruned
    }
    return result
  }

  return value
}

export function metaWriteFromDomain(fields: Record<string, unknown>): JsonObject {
  const encoded: Record<string, unknown> = {}
  for (const key in fields) {
    const value = fields[key]
    encoded[key] = value === undefined ? null : value
  }
  return pruneMeta(encoded) as JsonObject
}
